import React from 'react';
import { useTranslation } from 'react-i18next';
import { Car, ChevronDown, ChevronUp, Database, FileSpreadsheet } from 'lucide-react';
import SourceBadge from './SourceBadge';
import SupplierSuggestionRow, { SupplierSuggestion, trimSuggestionsForDisplay } from './SupplierSuggestionRow';

/** Rows shown per term before the "Show all N" toggle kicks in. */
// Max suggestion rows shown per term before collapsing (Ben, 2026-08-24:
// long lists drowned the results — everything past 2 hides behind Show all).
const VISIBLE_ROWS = 2;

/**
 * Standalone "Found in your data sources" block for turns with NO catalog
 * results at all (every requested part not-found / not-in-catalog). The
 * results table doesn't render on those turns, so the vector-search hits
 * carried by the message payload (`supplier_suggestions`) get this dedicated
 * card instead. Row layout + add-to-cart contract are shared with the
 * in-table strip via SupplierSuggestionRow.
 */
interface DataSourceSuggestionsProps {
  /** Vector-search hits keyed by the requested term. */
  suggestions: Record<string, SupplierSuggestion[]>;
  /** Vehicle context the backend ranked the suggestions with ("MG ZS"). */
  vehicle?: string | null;
  onAddToCart?: (part: any, structuredData: any) => void;
  structuredData?: any;
  cartItems?: any[];
}

const DataSourceSuggestions: React.FC<DataSourceSuggestionsProps> = ({
  suggestions, vehicle, onAddToCart, structuredData, cartItems,
}) => {
  const { t } = useTranslation();
  const [addingIds, setAddingIds] = React.useState<Set<string>>(new Set());
  const [expandedTerms, setExpandedTerms] = React.useState<Set<string>>(new Set());

  const inCartIds = React.useMemo(() => {
    const set = new Set<string>();
    cartItems?.forEach((item: any) => {
      const pid = item.structured_data?.part_data?.part_id;
      if (pid) set.add(String(pid).toLowerCase());
    });
    return set;
  }, [cartItems]);

  const handleAdd = async (sugg: SupplierSuggestion, term: string) => {
    if (!onAddToCart) return;
    setAddingIds(prev => new Set(prev).add(sugg.suggestion_id));
    try {
      // Same cart contract as MultiPartResultView.handleAddSuggestionToCart:
      // pre-priced datasource line (pricing_data attached, DMS pricing skipped).
      await onAddToCart({
        partId: sugg.suggestion_id,
        partNumber: sugg.part_number || undefined,
        partName: sugg.name,
        description: sugg.name,
        price: sugg.price != null ? String(sugg.price) : undefined,
        price_source: 'datasource',
        supplier_name: sugg.supplier_name || sugg.source_name || undefined,
        currency: sugg.currency || undefined,
        related_user_query: term,
        pricing_data: sugg.price != null ? [{
          price: sugg.price,
          ...(sugg.currency ? { currency: sugg.currency } : {}),
          supplier: sugg.supplier_name || sugg.source_name || undefined,
          source: 'datasource',
        }] : [],
      }, structuredData);
    } finally {
      setAddingIds(prev => { const next = new Set(prev); next.delete(sugg.suggestion_id); return next; });
    }
  };

  // Strong best match (≥80%) → only the top 3 per term; weak lists stay full.
  const entries = Object.entries(suggestions || {})
    .map(([term, hits]) => [term, trimSuggestionsForDisplay(hits || [])] as [string, SupplierSuggestion[]])
    .filter(([, hits]) => hits && hits.length > 0);
  if (entries.length === 0) return null;

  // When every hit comes from ONE source (the common case), name it once in
  // the header instead of repeating it on every row.
  const sourceNames = Array.from(new Set(
    entries.flatMap(([, hits]) => hits.map(s => (s.source_name || '').trim()).filter(Boolean)),
  ));
  const singleSource = sourceNames.length === 1 ? sourceNames[0] : null;
  const totalHits = entries.reduce((n, [, hits]) => n + hits.length, 0);

  const toggleTerm = (term: string) => setExpandedTerms(prev => {
    const next = new Set(prev);
    if (next.has(term)) next.delete(term); else next.add(term);
    return next;
  });

  return (
    <div className="mt-2" style={{ backgroundColor: '#faf9f7', border: '1px solid #ebe7df', borderRadius: '12px', padding: '10px 14px 8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', marginBottom: '4px' }}>
        <Database size={13} color="#7c6f5a" />
        <span style={{ fontSize: '11px', fontWeight: 700, color: '#7c6f5a', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          {t('chat.multiPart.dataSourceSuggestions', { defaultValue: 'Found in your data sources' })}
          <SourceBadge kind="datasource" compact />
        </span>
        <span style={{ fontSize: '10px', fontWeight: 600, color: '#9a958c' }}>
          {t('chat.multiPart.suggestionMatches', { n: totalHits, defaultValue: '{{n}} matches' })}
        </span>
        {!!(vehicle || '').trim() && (
          <span
            title={t('chat.multiPart.suggestionsVehicleHint', { defaultValue: 'Suggestions are ranked using your vehicle details' })}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: 600, color: '#55524b', backgroundColor: '#efece6', borderRadius: '20px', padding: '1px 8px' }}
          >
            <Car size={11} style={{ flexShrink: 0 }} />
            {t('chat.multiPart.suggestionsVehicle', { vehicle: (vehicle || '').trim(), defaultValue: 'Matched for {{vehicle}}' })}
          </span>
        )}
        {singleSource && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: 'auto', fontSize: '10px', fontWeight: 600, color: '#55524b', backgroundColor: '#efece6', borderRadius: '20px', padding: '2px 8px', maxWidth: '200px' }}>
            <FileSpreadsheet size={11} style={{ flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{singleSource}</span>
          </span>
        )}
      </div>
      {entries.map(([term, hits], ei) => {
        const expanded = expandedTerms.has(term);
        const visible = expanded ? hits : hits.slice(0, VISIBLE_ROWS);
        const hiddenCount = hits.length - VISIBLE_ROWS;
        return (
          <div key={term} style={{ marginTop: ei > 0 ? '10px' : 0 }}>
            {entries.length > 1 && (
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '2px' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#55524b' }}>{term}</span>
                <span style={{ fontSize: '10px', color: '#9a958c' }}>
                  {t('chat.multiPart.suggestionMatches', { n: hits.length, defaultValue: '{{n}} matches' })}
                </span>
              </div>
            )}
            {visible.map((sugg, si) => (
              <SupplierSuggestionRow
                key={sugg.suggestion_id}
                sugg={sugg}
                withDivider={si > 0}
                hideSupplier={!!singleSource && (sugg.supplier_name || '').trim() === singleSource}
                inCart={inCartIds.has(sugg.suggestion_id.toLowerCase())}
                adding={addingIds.has(sugg.suggestion_id)}
                onAdd={onAddToCart ? () => handleAdd(sugg, term) : undefined}
              />
            ))}
            {hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => toggleTerm(term)}
                className="transition-colors hover:bg-[#efece6]"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '2px', fontSize: '10px', fontWeight: 600, color: '#7c6f5a', backgroundColor: 'transparent', border: 'none', borderRadius: '6px', padding: '3px 8px', cursor: 'pointer' }}
              >
                {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                {expanded
                  ? t('chat.multiPart.showLessSuggestions', { defaultValue: 'Show less' })
                  : t('chat.multiPart.showAllSuggestions', { n: hits.length, defaultValue: 'Show all {{n}}' })}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default DataSourceSuggestions;
